import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  DollarSign,
  TrendingUp,
  Percent,
  Target,
  Gauge,
  ShoppingCart,
  Receipt,
  Trophy,
  Users,
  BarChart3,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { KpiCard } from "@/components/dashboard/KpiCard";
import { PeriodSelector } from "@/components/dashboard/PeriodSelector";
import { EvolutionChart } from "@/components/dashboard/EvolutionChart";
import { DeltaBadge } from "@/components/dashboard/DeltaBadge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { LoadingState, ErrorState } from "@/components/common/StateViews";
import { fmtBRL, fmtPct, fmtNum, PERIODS } from "@/lib/format";

const fetchRaiox = ({ id, period }) =>
  api.get(`/salespeople/${id}/raiox`, { params: { period } }).then((r) => r.data);

export default function RaioX() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["raiox", id, period],
    queryFn: () => fetchRaiox({ id, period }),
    keepPreviousData: true,
  });

  const periodLabel = PERIODS.find((p) => p.value === period)?.label;

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Button
            data-testid="raiox-back-button"
            variant="ghost"
            size="sm"
            className="mb-2 gap-2 px-2 text-muted-foreground"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar ao dashboard
          </Button>
          <h1 data-testid="raiox-title" className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            {data?.salesperson?.name || "Raio-X"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Raio-X do vendedor · {periodLabel}
          </p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {isLoading && <LoadingState label="Carregando raio-x..." />}
      {isError && (
        <ErrorState
          message={formatApiErrorDetail(error?.response?.data?.detail)}
          onRetry={refetch}
        />
      )}

      {data && !isError && (
        <div className={isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}>
          {/* Comparação com a equipe */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-md border border-border bg-card p-6" data-testid="raiox-posicao">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Trophy className="h-4 w-4" /> Posição no ranking
              </div>
              <div className="mt-3 font-num text-2xl font-semibold">
                {data.comparison.posicao}º
                <span className="ml-1 text-base text-muted-foreground">
                  / {data.comparison.total_vendedores}
                </span>
              </div>
            </div>
            <div className="rounded-md border border-border bg-card p-6" data-testid="raiox-vs-media">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Users className="h-4 w-4" /> Vs. média da equipe
              </div>
              <div className="mt-3 flex items-center gap-2 font-num text-2xl font-semibold">
                <DeltaBadge value={data.comparison.vs_media_pct} className="text-base" />
              </div>
              <p className="mt-1 font-num text-xs text-muted-foreground">
                Média: {fmtBRL(data.comparison.media_receita_equipe)}
              </p>
            </div>
            <div className="rounded-md border border-border bg-card p-6" data-testid="raiox-atingimento">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Target className="h-4 w-4" /> Atingimento da meta
              </div>
              <div className="mt-3 font-num text-2xl font-semibold">
                {fmtPct(data.kpis.atingimento)}
              </div>
              <Progress value={Math.min(data.kpis.atingimento, 100)} className="mt-2 h-2" />
            </div>
          </div>

          {/* KPIs */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard testId="raiox-kpi-receita" title="Receita" value={fmtBRL(data.kpis.receita)} icon={DollarSign} delta={data.delta.receita} footer="vs. anterior" />
            <KpiCard testId="raiox-kpi-lucro" title="Lucro" value={fmtBRL(data.kpis.lucro)} icon={TrendingUp} delta={data.delta.lucro} footer="vs. anterior" />
            <KpiCard testId="raiox-kpi-margem" title="Margem" value={fmtPct(data.kpis.margem)} icon={Percent} delta={data.delta.margem} deltaSuffix="pp" footer="vs. anterior" />
            <KpiCard testId="raiox-kpi-meta" title="Meta" value={fmtBRL(data.kpis.meta)} icon={Target} />
            <KpiCard testId="raiox-kpi-pace" title="Pace (projeção)" value={fmtBRL(data.kpis.pace)} icon={Gauge} />
            <KpiCard testId="raiox-kpi-num-vendas" title="Nº de vendas" value={fmtNum(data.kpis.num_vendas)} icon={ShoppingCart} />
            <KpiCard testId="raiox-kpi-ticket" title="Ticket médio" value={fmtBRL(data.kpis.ticket_medio)} icon={Receipt} />
            <KpiCard testId="raiox-kpi-custo" title="Custo" value={fmtBRL(data.kpis.custo)} icon={Receipt} />
          </div>

          {/* Evolução */}
          <div className="mt-6 rounded-md border border-border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-base font-semibold">
                Evolução (últimos 6 meses)
              </h2>
            </div>
            <EvolutionChart data={data.evolution} />
          </div>
        </div>
      )}
    </AppLayout>
  );
}
